import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, IdentificationCard, CheckCircle, Lock } from "@phosphor-icons/react";

export default function KYC() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const [form, setForm] = useState({ full_name: user?.name || "", pan: "", aadhaar: "" });
  const [loading, setLoading] = useState(false);
  const verified = user?.kyc_status === "verified";

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-navy-950 placeholder:text-gray-400 focus:border-gold focus:ring-1 focus:ring-gold focus:outline-none transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/kyc/submit", form);
      await refresh();
      toast.success("KYC verified");
      navigate(next);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setLoading(false); }
  };

  return (
    <div data-testid="kyc-page" className="px-6 md:px-12 lg:px-20 py-14 md:py-20">
      <div className="max-w-2xl mx-auto">
        <p className="eyebrow mb-2">Identity Verification</p>
        <h1 className="font-display text-4xl md:text-5xl text-white">Complete your KYC.</h1>
        <p className="text-ebg-muted mt-2">A one-time verification required before you can invest. Your data is encrypted and stored securely.</p>

        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-2xl mt-8">
          {verified ? (
            <div className="text-center py-6" data-testid="kyc-verified-state">
              <CheckCircle size={56} weight="fill" className="text-gold mx-auto" />
              <h2 className="font-display text-3xl text-navy-950 mt-4">You're verified.</h2>
              <p className="text-gray-500 mt-2">Your identity has been verified. You can now invest in EBG brands.</p>
              <Link to={next} className="inline-block mt-6 btn-gold px-7 py-3 rounded-lg">Continue</Link>
            </div>
          ) : (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-6">
                <p className="text-xs text-amber-700">DEMO MODE — verification is simulated (no real KYC provider connected yet).</p>
              </div>
              <form onSubmit={submit} className="space-y-4" data-testid="kyc-form">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1.5 block">Full Name (as per PAN)</label>
                  <input data-testid="kyc-name-input" required value={form.full_name} onChange={set("full_name")} className={inputCls} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1.5 block flex items-center gap-1.5"><IdentificationCard size={15} /> PAN Number</label>
                  <input data-testid="kyc-pan-input" required maxLength={10} placeholder="ABCDE1234F" value={form.pan} onChange={set("pan")} className={`${inputCls} uppercase`} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1.5 block flex items-center gap-1.5"><Lock size={15} /> Aadhaar Number</label>
                  <input data-testid="kyc-aadhaar-input" required maxLength={12} placeholder="12-digit Aadhaar" value={form.aadhaar} onChange={set("aadhaar")} className={inputCls} />
                  <p className="text-[0.7rem] text-gray-400 mt-1">Only the last 4 digits are retained.</p>
                </div>
                <button data-testid="kyc-submit-btn" disabled={loading} className="w-full btn-gold py-3 rounded-lg disabled:opacity-50">{loading ? "Verifying…" : "Verify Identity"}</button>
              </form>
              <p className="text-center text-xs text-gray-400 mt-5 flex items-center justify-center gap-1.5"><ShieldCheck size={14} className="text-gold" /> 256-bit encrypted · DPDP compliant</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
